/**
 * PublicLayout — top nav + minimal footer for PUBLIC (unauthenticated)
 * pages. Used by Landing and the signed-out variant of About.
 *
 * Signed-in users see a richer floating island nav (MainLayout). This
 * layout is intentionally minimal — logo + About + Pricing + Sign in + Join
 * the waitlist (About and Pricing hide under sm; the footer keeps both).
 * Sign-up is by invite, so the one public way in is the waitlist.
 * Kept as a separate component (rather than composed into Landing) so
 * /about can render with the same public chrome when visited by a
 * signed-out user.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/outline';
import bearriLogo from '../assets/images/bearri_art/bearri_bear_logo.png';
import { MADE_WITH } from '../components/brand/makers';
// The paper material system for public pages (tokens + pp-* classes). Imported
// here (and in Login) so it only ships in public chunks, never in /app.
import '../styles/paper.css';

// A cream paper strip connects the forest to the rest of the public page.
export const PublicTopNav: React.FC = () => (
  <header className="pp-nav">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
      <Link to="/" className="flex shrink-0 items-center gap-2">
        <img src={bearriLogo} alt="" className="pp-nav__logo h-7 w-auto" />
        <span className="font-display text-xl font-bold text-text-primary">Bearri</span>
      </Link>
      <nav className="flex shrink-0 items-center gap-1 sm:gap-3">
        <Link to="/about" className="pp-nav__link hidden sm:inline-flex">
          About
        </Link>
        <Link to="/#pricing" className="pp-nav__link hidden sm:inline-flex">
          Pricing
        </Link>
        <Link to="/login" className="pp-nav__link">
          Sign in
        </Link>
        <Link to="/waitlist" className="pp-btn pp-btn--blue pp-btn--sm">
          Join the waitlist
          <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
        </Link>
      </nav>
    </div>
  </header>
);

// The footer closes the paper scene with a torn cream edge into navy.
// Its links ring in cream: paper.css's default light-blue ring is about 2.7:1 on the navy board, under the 3:1 a focus
// indicator needs; cream on navy is about 8:1. `!` because `.pp-root :focus-visible` has the same specificity and
// paper.css loads after Tailwind.
const FOOTER_FOCUS = 'focus-visible:!outline-[color:var(--pp-cream)]';

export const PublicFooter: React.FC = () => (
  <footer className="pp-footer pp-torn-top">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 pp-footer__inner">
      <div className="flex items-center gap-2">
        <img src={bearriLogo} alt="" className="h-5 w-auto" style={{ filter: 'drop-shadow(0 1px 0 rgba(0,0,0,.45))' }} />
        <span className="pp-footer__copy">&copy; 2026 Bearri · {MADE_WITH}</span>
      </div>
      <nav className="pp-footer__nav">
        <Link to="/about" className={FOOTER_FOCUS}>About</Link>
        <Link to="/#pricing" className={FOOTER_FOCUS}>Pricing</Link>
        <a href="mailto:support@bearri.co" className={FOOTER_FOCUS}>Contact</a>
        <Link to="/privacy" className={FOOTER_FOCUS}>Privacy</Link>
        <Link to="/terms" className={FOOTER_FOCUS}>Terms</Link>
      </nav>
    </div>
  </footer>
);

const PublicLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="pp-root min-h-screen flex flex-col">
    <PublicTopNav />
    {/* No width constraint here on purpose — Landing renders full-bleed
        sections through this layout. Pages that want the app's content width
        (About) wrap themselves; see AboutWrapper in App.tsx. */}
    <main className="flex-1">{children}</main>
    <PublicFooter />
  </div>
);

export default PublicLayout;
